import React, { useRef, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import ThreeDotIcon from '../../../assets/threedot-icon.svg';
import { GET_DISMISS, GET_MARK_AS_READ, GET_MARK_AS_UNREAD } from '../../../apis/notification.api';
import { getDisplayName } from './utils/notification-helpers';
import NotificationTypeRenderer from './NotificationTypeRenderer';

/**
 * Parent component that handles common notification logic:
 * - Mark as read/unread
 * - Dismiss
 * - Navigation
 * - Three-dot menu
 *
 * Delegates content rendering to child components via NotificationTypeRenderer
 */
const NotificationItem = ({ elm, setListNotification, index, setPagination }) => {
  const navigate = useNavigate();
  const [currentElm, setCurrentElm] = useState(elm);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  const buttonRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleOutsideClick = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    setCurrentElm(elm);
  }, [elm]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 160 + window.scrollX // 160px is the dropdown width (w-40)
      });
    }
  }, [isOpen]);

  const markAsRead = async () => {
    if (!currentElm.markAsRead) {
      const response = await fetch(`${GET_MARK_AS_READ}/${currentElm.id}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.success) {
        setCurrentElm((pre) => ({ ...pre, markAsRead: true }));
        setPagination((pre) => ({ ...pre, unreadCount: pre.unreadCount - 1 }));
      }
    } else {
      const response = await fetch(`${GET_MARK_AS_UNREAD}/${currentElm.id}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.success) {
        setCurrentElm((pre) => ({ ...pre, markAsRead: false }));
        setPagination((pre) => ({ ...pre, unreadCount: pre.unreadCount + 1 }));
      }
    }
  };

  const handleRedirect = (e) => {
    e.stopPropagation();
    if (!currentElm.markAsRead) {
      markAsRead();
    }

    const redirectUrl = currentElm.redirectUrl;

    if (!redirectUrl) {
      return;
    }

    const isExternalLink = /^https?:\/\//i.test(redirectUrl);

    if (isExternalLink) {
      window.location.href = redirectUrl;
      return;
    }

    navigate(redirectUrl, { replace: false });
  };

  const handleDismiss = async () => {
    const response = await fetch(`${GET_DISMISS}/${currentElm.id}`, {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json'
      }
    });
    const result = await response.json();
    if (result.success) {
      setListNotification((prev) => {
        const newList = [...prev];
        newList.splice(index, 1);
        return newList;
      });
      if (!currentElm.markAsRead) {
        setPagination((prev) => ({
          ...prev,
          unreadCount: prev.unreadCount - 1
        }));
      }
      setIsOpen(false);
    }
  };

  const displayName = getDisplayName(currentElm);

  const handleMarkAsRead = (e) => {
    e.stopPropagation();
    markAsRead();
    setIsOpen(false);
  };

  const handleDismissClick = (e) => {
    e.stopPropagation();
    handleDismiss();
    setIsOpen(false);
  };

  return (
    <div
      className={`w-full rounded-md p-0 relative ${currentElm.markAsRead ? '' : 'bg-[#EDF7EE]'} mb-1 p-2 cursor-pointer transition-all`}
      onClick={(e) => handleRedirect(e)}
    >
      <div className="min-w-0">
        <NotificationTypeRenderer
          notification={currentElm}
          displayName={displayName}
          subIcon={currentElm.subIcon}
        />
      </div>

      {/* Three-dot menu */}
      <div className="absolute top-1 right-1" ref={ref}>
        <div
          ref={buttonRef}
          className={`w-[24px] h-[24px] flex items-center justify-center rounded-full hover:opacity-30 cursor-pointer transition`}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
        >
          <img src={ThreeDotIcon} alt="more option" className="w-[20px] h-[20px]" />
        </div>

        {isOpen && (
          <div
            className="fixed w-40 p-2 rounded-[12px] shadow-lg border border-gray-200 bg-white flex flex-col gap-1 z-50"
            style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
          >
            <div
              className="py-[6px] px-[14px] hover:bg-[#A6D38D] rounded-md transition-all cursor-pointer"
              onClick={handleMarkAsRead}
            >
              <span className="text-sm font-[900]">
                {currentElm.markAsRead ? 'Mark as Unread' : 'Mark as Read'}
              </span>
            </div>
            <div
              className="py-[6px] px-[14px] hover:bg-[#A6D38D] rounded-md transition-all cursor-pointer"
              onClick={handleDismissClick}
            >
              <span className="text-sm font-[900]">Dismiss</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
